import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { ShoppingCart, User, Menu, X, LogOut, Search, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { useMetalRates } from '../context/MetalRatesContext';
import { motion, AnimatePresence } from 'framer-motion';

const LOGO_URL = '/marissa_logo.png';

export default function Navbar() {
  const { user, logout } = useAuth();
  const { cart } = useCart();
  const { metalRates: allRates } = useMetalRates();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [currentRateIndex, setCurrentRateIndex] = useState(0);

  const metalRates = allRates.filter(r => r.show_on_homepage);
  const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    if (metalRates.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentRateIndex((prev) => (prev + 1) % metalRates.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [metalRates.length]);

  const handleLogout = () => {
    logout();
    navigate('/');
    setUserMenuOpen(false);
  };

  const categories = [
    { name: 'Gold', path: '/products?category=gold' },
    { name: 'Silver', path: '/products?category=silver' },
    { name: 'Diamond', path: '/products?category=diamond' },
    { name: 'Platinum', path: '/products?category=platinum' },
    { name: 'Silver Bar', path: '/silver-rates' },
  ];

  return (
    <>
      <nav className="sticky top-0 z-50 bg-[#FEFAF3]/80 backdrop-blur-md" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-12">
          <div className="flex items-center justify-between h-[60px] md:h-[80px]">

            {/* Left: Menu + Search (Desktop: text links, Mobile: icons) */}
            <div className="flex items-center space-x-4 md:space-x-6 w-1/3">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="flex items-center space-x-1.5 text-[#1a1a1a] hover:text-[#666] transition-colors"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
                <span className="hidden md:inline text-xs tracking-widest uppercase font-medium">Menu</span>
              </button>
              <Link to="/products" className="flex items-center space-x-1.5 text-[#1a1a1a] hover:text-[#666] transition-colors" data-testid="nav-search">
                <Search className="h-5 w-5" />
                <span className="hidden md:inline text-xs tracking-widest uppercase font-medium">Search</span>
              </Link>
            </div>

            {/* Center: Logo */}
            <div className="flex-shrink-0">
              <Link to="/" className="flex items-center justify-center" data-testid="home-link">
                <img
                  src={LOGO_URL}
                  alt="Marissa Gold"
                  className="h-10 w-auto md:h-14 lg:h-16 object-contain"
                />
              </Link>
            </div>

            {/* Right: Icons */}
            <div className="flex items-center justify-end space-x-4 md:space-x-5 w-1/3">
              {/* Desktop: Rate ticker */}
              {isHomePage && metalRates.length > 0 && (
                <div className="hidden lg:flex items-center space-x-1.5">
                  <TrendingUp className="h-3.5 w-3.5 text-[#999]" />
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentRateIndex}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -6 }}
                      transition={{ duration: 0.2 }}
                      className="text-[11px] text-[#1a1a1a] font-bold tracking-wide"
                    >
                      {metalRates[currentRateIndex]?.display_label || `${metalRates[currentRateIndex]?.purity || ''} ${metalRates[currentRateIndex]?.metal_type}`}: ₹{(metalRates[currentRateIndex]?.rate_per_gram || 0).toLocaleString('en-IN')}/g
                    </motion.span>
                  </AnimatePresence>
                </div>
              )}

              {/* User */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    className="text-[#1a1a1a] hover:text-[#666] transition-colors"
                    data-testid="user-menu-button"
                  >
                    <User className="h-5 w-5 md:h-[22px] md:w-[22px]" strokeWidth={1.5} />
                  </button>
                  <AnimatePresence>
                    {userMenuOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="absolute right-0 mt-3 w-48 bg-white rounded-lg shadow-xl py-2 border border-gray-100"
                        data-testid="user-dropdown"
                      >
                        {user.role === 'admin' ? (
                          <>
                            <Link to="/admin" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setUserMenuOpen(false)} data-testid="admin-dashboard-link">Admin Dashboard</Link>
                            <Link to="/analytics" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setUserMenuOpen(false)} data-testid="analytics-link">Analytics</Link>
                          </>
                        ) : (
                          <Link to="/dashboard" className="block px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" onClick={() => setUserMenuOpen(false)} data-testid="customer-dashboard-link">My Orders</Link>
                        )}
                        <button onClick={handleLogout} className="w-full text-left px-4 py-2.5 text-sm text-red-600 hover:bg-gray-50 flex items-center space-x-2" data-testid="logout-button">
                          <LogOut className="h-4 w-4" /><span>Logout</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ) : (
                <Link to="/login" className="text-[#1a1a1a] hover:text-[#666] transition-colors" data-testid="login-button">
                  <User className="h-5 w-5 md:h-[22px] md:w-[22px]" strokeWidth={1.5} />
                </Link>
              )}

              {/* Cart */}
              <Link to="/cart" className="relative text-[#1a1a1a] hover:text-[#666] transition-colors" data-testid="cart-button">
                <ShoppingCart className="h-5 w-5 md:h-[22px] md:w-[22px]" strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span className="absolute -top-1.5 -right-1.5 bg-[#1a1a1a] text-white text-[9px] rounded-full h-4 w-4 flex items-center justify-center font-medium" data-testid="cart-count">
                    {cartCount}
                  </span>
                )}
              </Link>
            </div>
          </div>
        </div>

        {/* Mobile Rate Ticker */}
        {metalRates.length > 0 && (
          <div className="md:hidden bg-[#F5F5F5] overflow-hidden">
            <div className="flex items-center h-6 animate-marquee whitespace-nowrap">
              <div className="flex items-center space-x-6 px-4">
                {metalRates.map((rate) => (
                  <div key={`ticker-${rate.metal_type}-${rate.purity}`} className="flex items-center space-x-1.5">
                    <TrendingUp className="h-3 w-3 text-[#999]" />
                    <span className="text-[10px] text-[#1a1a1a] font-bold">{rate.display_label || `${rate.purity || ''} ${rate.metal_type}`}: ₹{(rate.rate_per_gram || 0).toLocaleString('en-IN')}/g</span>
                  </div>
                ))}
                {metalRates.map((rate, index) => (
                  <div key={`dup-${rate.metal_type}-${rate.purity}-${index}`} className="flex items-center space-x-1.5">
                    <TrendingUp className="h-3 w-3 text-[#999]" />
                    <span className="text-[10px] text-[#1a1a1a] font-bold">{rate.display_label || `${rate.purity || ''} ${rate.metal_type}`}: ₹{(rate.rate_per_gram || 0).toLocaleString('en-IN')}/g</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Dropdown Menu */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="border-t border-gray-100 bg-white"
              data-testid="mobile-menu"
            >
              <div className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 md:grid-cols-3 gap-2 md:gap-4">
                {categories.map((cat) => (
                  <Link
                    key={cat.name}
                    to={cat.path}
                    className="block py-3 text-sm tracking-wide text-[#1a1a1a] hover:text-[#999] transition-colors uppercase font-medium"
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`nav-${cat.name.toLowerCase()}`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </>
  );
}
